import { CircleAlert, Copy, Pencil, RefreshCcw, Square, Sparkles } from 'lucide-react';
import { messageImage, messageText } from '../lib/chat-content';
import type { ChatMessage } from '../types/chat';
import { MarkdownContent } from './MarkdownContent';
import { ModelIcon } from './ModelIcon';
import { ReasoningPanel } from './ReasoningPanel';

type MessageItemProps = {
  message: ChatMessage;
  onCopy: (message: ChatMessage) => void;
  onEdit: (message: ChatMessage) => void;
  onRegenerate: (message: ChatMessage) => void;
};

export function MessageItem({ message, onCopy, onEdit, onRegenerate }: MessageItemProps) {
  const text = messageText(message.content);
  const reasoning = message.reasoning?.trim() ?? '';
  const image = messageImage(message.content);
  const isUser = message.role === 'user';
  const isGeneratedImage = Boolean(message.imageGeneration);

  if (isUser) {
    return (
      <article className="flex w-full flex-col items-end">
        <div className="max-w-[680px] rounded-2xl rounded-tr-md border border-border bg-surface-raised px-4 py-3 text-[15px] leading-[1.55] text-primary">
          {text && <p className="whitespace-pre-wrap">{text}</p>}
          {image && (
            <figure className={`${text ? 'mt-3' : ''} overflow-hidden rounded-xl border border-border bg-canvas-subtle`}>
              <img src={image.url} alt={image.name || 'Gambar yang dikirim'} className="max-h-[280px] w-full max-w-[360px] object-contain" />
              {image.name && <figcaption className="px-3 py-2 text-xs text-muted">{image.name}</figcaption>}
            </figure>
          )}
        </div>
        <button type="button" onClick={() => onEdit(message)} className="message-action mt-1.5"><Pencil className="size-3.5" />Edit</button>
      </article>
    );
  }

  const isThinking = message.status === 'thinking' && !text && !reasoning;
  return (
    <article className="w-full">
      <div className="mb-3 flex items-center gap-2 text-sm">
        <span className="grid size-6 place-items-center rounded-full bg-accent text-white">
          {message.modelAlias ? <ModelIcon alias={message.modelAlias} className="size-3.5" /> : <Sparkles className="size-3.5" />}
        </span>
        <span className="font-medium text-secondary">{message.modelName || 'Assistant'}</span>
        <span className="text-xs text-muted">• Asisten AI</span>
      </div>

      {message.status === 'error' ? (
        <div className="rounded-xl border border-danger/40 bg-danger/5 p-4 text-sm text-danger">
          <div className="flex items-start gap-2"><CircleAlert className="mt-0.5 size-4 shrink-0" /><span>{message.error || 'Gagal mendapatkan jawaban.'}</span></div>
          <button type="button" onClick={() => onRegenerate(message)} className="interactive mt-4 flex h-9 items-center gap-2 rounded-lg border border-danger px-3 font-medium hover:bg-danger/10">
            <RefreshCcw className="size-4" />Coba lagi
          </button>
        </div>
      ) : isThinking ? (
        <div className="flex h-12 items-center gap-3 rounded-xl border border-border bg-surface-raised px-4 text-sm text-muted">
          <span className="flex gap-1"><i className="thinking-dot" /><i className="thinking-dot" /><i className="thinking-dot" /></span>
          AI sedang berpikir…
        </div>
      ) : (
        <>
          {reasoning && !text && (
            <ReasoningPanel content={reasoning} streaming={message.status === 'streaming'} />
          )}
          {text && <MarkdownContent value={text} />}
          {image && (
            <figure className={`${text ? 'mt-4' : ''} image-result-card`}>
              <div className="image-result-frame">
                <img src={image.url} alt={image.name || 'Gambar buatan AI'} className="image-result-media" />
              </div>
              <figcaption className="image-result-footer">
                <span className="min-w-0 truncate">{isGeneratedImage ? 'Gambar AI' : image.name || 'Gambar buatan AI'}</span>
                <a href={image.url} target="_blank" rel="noreferrer" className="image-result-open">Buka gambar</a>
              </figcaption>
            </figure>
          )}
          {message.status === 'streaming' && text && (
            <div className="mt-4 flex items-center gap-2 text-xs text-muted">
              Sedang membuat jawaban… <span className="stream-cursor" />
            </div>
          )}
          {message.status === 'stopped' && (
            <div className="mt-3 flex items-center gap-2 text-xs text-muted"><Square className="size-3" />Jawaban dihentikan</div>
          )}
          {message.status !== 'streaming' && message.status !== 'thinking' && (
            <div className="mt-3 flex items-center gap-3">
              <button type="button" onClick={() => onCopy(message)} className="message-action"><Copy className="size-3.5" />Salin</button>
              <button type="button" onClick={() => onRegenerate(message)} className="message-action"><RefreshCcw className="size-3.5" />{isGeneratedImage ? 'Buat ulang gambar' : 'Regenerasi'}</button>
            </div>
          )}
        </>
      )}
    </article>
  );
}
